import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, ClipboardList, HandCoins, MapPinned, Pencil, Plus, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, StatusPill, UsernameInput } from "@/components/common";
import { ResortSelect } from "@/pages/StaffPage";
import { rupiah, toEmail } from "@/lib/format";

const ResortForm = ({ open, onOpenChange, initial, petugas, onSaved }) => {
  const [f, setF] = useState({ name: "", description: "", petugas_id: "none" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { name: initial.name, description: initial.description || "", petugas_id: initial.petugas_id || "none" } : { name: "", description: "", petugas_id: "none" }); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, petugas_id: f.petugas_id === "none" ? null : f.petugas_id };
    try {
      if (initial) await api.put(`/resorts/${initial.id}`, body); else await api.post("/resorts", body);
      toast.success(initial ? "Resort diperbarui" : "Resort dibuat"); onOpenChange(false); onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="resort-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Ubah Resort" : "Resort Baru"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nama Resort"><Input required minLength={2} className="h-11" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="resort-name-input" /></Field>
          <Field label="Petugas Lapangan" hint="Satu resort dikelola satu petugas; satu petugas boleh beberapa resort.">
            <Select value={f.petugas_id} onValueChange={(v) => setF({ ...f, petugas_id: v })}>
              <SelectTrigger className="h-11" data-testid="resort-petugas-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Belum ditentukan</SelectItem>
                {petugas.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`resort-petugas-option-${p.id}`}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Cakupan Wilayah (opsional)"><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="resort-desc-input" /></Field>
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full h-11 px-6" data-testid="resort-form-submit">{busy ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PetugasForm = ({ open, onOpenChange, resorts, preset, onSaved }) => {
  const [f, setF] = useState({ name: "", email: "", password: "", resort_id: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ name: "", email: "", password: "", resort_id: preset || resorts[0]?.id || "" }); }, [open, preset, resorts]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.resort_id) { toast.error("Pilih resort terlebih dahulu"); return; }
    setBusy(true);
    try { await api.post("/resorts/petugas", { ...f, email: toEmail(f.email) }); toast.success("Petugas lapangan ditambahkan"); onOpenChange(false); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="resort-petugas-dialog">
        <DialogHeader><DialogTitle className="font-heading">Tambah Petugas Lapangan</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Resort"><ResortSelect value={f.resort_id} onChange={(v) => setF({ ...f, resort_id: v })} resorts={resorts} allowNone={false} testId="new-petugas-resort-select" /></Field>
          <Field label="Nama Lengkap"><Input required className="h-11" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-petugas-name-input" /></Field>
          <Field label="Username Login" hint={`Email login otomatis: ${f.email || "nama_akun"}@kspbam.com`}><UsernameInput value={f.email} onChange={(v) => setF({ ...f, email: v })} required testId="new-petugas-email-input" /></Field>
          <Field label="Password" hint="Minimal 8 karakter"><Input type="password" required minLength={8} className="h-11" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-petugas-password-input" /></Field>
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full h-11 px-6" data-testid="new-petugas-submit">{busy ? "Menyimpan..." : "Tambah Petugas"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ResortCard = ({ r, onEdit, onToggle, onAdd, onReport }) => (
  <div className="panel !p-5 flex flex-col gap-4 min-w-0" data-testid={`resort-card-${r.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className="h-11 w-11 rounded-xl bg-primary/12 text-primary grid place-items-center shrink-0"><MapPinned className="h-5 w-5" /></span>
        <div className="min-w-0"><p className="font-heading font-semibold truncate">{r.name}</p><p className="text-xs text-muted-foreground line-clamp-2">{r.description || "-"}</p></div>
      </div>
      <StatusPill active={r.active} />
    </div>
    <div className="rounded-xl bg-muted/40 px-3 py-2 text-sm" data-testid={`resort-petugas-${r.id}`}>Petugas: <b>{r.petugas?.name || "Belum ditentukan"}</b></div>
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span className="flex items-center gap-1" data-testid={`resort-members-${r.id}`}><Users className="h-3.5 w-3.5" /><b className="text-foreground">{r.member_count}</b> anggota</span>
      <span className="flex items-center gap-1" data-testid={`resort-loans-${r.id}`}><HandCoins className="h-3.5 w-3.5" /><b className="text-foreground">{r.loan_count || 0}</b> pinjaman · <b className="text-foreground font-mono">{rupiah(r.loan_principal || 0)}</b></span>
    </div>
    <div className="mt-auto pt-4 border-t flex flex-nowrap items-center gap-1.5 overflow-x-auto">
      <Button size="sm" variant="outline" className="h-9 w-9 p-0 rounded-full shrink-0" onClick={() => onEdit(r)} aria-label="Edit resort" title="Edit" data-testid={`resort-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="sm" className="h-9 w-9 p-0 rounded-full shrink-0" onClick={() => onReport(r)} aria-label="Laporan resort" title="Laporan" data-testid={`resort-report-${r.id}`}><BarChart3 className="h-4 w-4" /></Button>
      <Button asChild size="sm" variant="outline" className="h-9 rounded-full px-3 text-xs shrink-0"><Link to={`/resort/${r.id}/tagihan`} data-testid={`resort-tagihan-${r.id}`}><ClipboardList className="h-3.5 w-3.5 mr-1 hidden sm:inline" />Tagihan</Link></Button>
      {!r.petugas && <Button size="sm" variant="ghost" className="h-9 w-9 p-0 rounded-full shrink-0" onClick={() => onAdd(r.id)} disabled={!r.active} data-testid={`resort-add-petugas-${r.id}`}><UserPlus className="h-4 w-4" /></Button>}
      <Switch className="ml-auto shrink-0" checked={r.active} onCheckedChange={(v) => onToggle(r, v)} data-testid={`resort-toggle-${r.id}`} />
    </div>
  </div>
);

export default function ResortPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ open: false, initial: null });
  const [pForm, setPForm] = useState({ open: false, preset: null });
  const load = useCallback(() => { api.get("/resorts").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const free = (data?.resorts || []).filter((r) => r.active && !r.petugas);
  const onToggle = async (r, v) => { try { await api.patch(`/resorts/${r.id}/status`, { active: v }); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHeader title="Resort"
        action={<div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full h-11 px-5" disabled={!free.length} onClick={() => setPForm({ open: true, preset: null })} data-testid="add-resort-petugas-button"><UserPlus className="h-4 w-4 mr-2" />Tambah Petugas Lapangan</Button>
          <Button className="rounded-full h-11 px-5" onClick={() => setForm({ open: true, initial: null })} data-testid="add-resort-button"><Plus className="h-4 w-4 mr-2" />Resort Baru</Button>
        </div>} />
      {!data ? <p className="text-muted-foreground">Memuat...</p> : data.resorts.length === 0 ? <EmptyState icon={MapPinned} title="Belum ada resort" testId="resorts-empty" /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {data.resorts.map((r) => <ResortCard key={r.id} r={r} onEdit={(x) => setForm({ open: true, initial: x })} onToggle={onToggle} onAdd={(id) => setPForm({ open: true, preset: id })} onReport={(x) => navigate(`/resort/${x.id}`)} />)}
        </div>
      )}
      <ResortForm open={form.open} initial={form.initial} petugas={data?.petugas || []} onOpenChange={(o) => setForm({ ...form, open: o })} onSaved={load} />
      <PetugasForm open={pForm.open} preset={pForm.preset} resorts={free} onOpenChange={(o) => setPForm({ ...pForm, open: o })} onSaved={load} />
    </div>
  );
}
